import { useState, useEffect } from "react";
import { useAuth } from "../contexts/AuthContext";
import { useCurrency } from "../contexts/CurrencyContext";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "./ui/popover";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { DollarSign, Save, Loader2 } from "lucide-react";
import axios from "axios";
import toast from "react-hot-toast";

const API = window.location.origin;
const PRESETS = [1300, 1370, 1400, 1450, 1500];

const MODES = [
  { id: "iqd", label: "د.ع فقط", emoji: "🇮🇶", desc: "إخفاء الدولار" },
  { id: "both", label: "كلاهما", emoji: "🌐", desc: "د.ع + دولار" },
  { id: "usd", label: "دولار", emoji: "💵", desc: "إبراز الدولار" },
];

/**
 * Compact button in the header that shows current USD rate and lets the user
 * update it (and switch display mode) via a small popover.
 */
export default function CurrencyQuickEdit({ compact = false }) {
  const { token } = useAuth();
  const { rate, displayMode, refresh, setMode } = useCurrency();
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState(rate);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setValue(rate);
  }, [rate, open]);

  const save = async (newRate) => {
    const finalRate = Number(newRate);
    if (!finalRate || finalRate < 100) {
      toast.error("سعر صرف غير صحيح");
      return;
    }
    setSaving(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const current = await axios.get(`${API}/api/store-settings`, { headers });
      const payload = {
        ...current.data,
        store_name: current.data.store_name || "محلي",
        usd_exchange_rate: finalRate,
      };
      delete payload.user_id;
      delete payload.updated_at;
      const oldRate = current.data.usd_exchange_rate;
      await axios.post(`${API}/api/store-settings`, payload, { headers });
      // audit log if rate actually changed
      if (oldRate && Math.abs(oldRate - finalRate) > 0.5) {
        axios.post(`${API}/api/audit/log`, {
          action: "currency_rate_change",
          entity_type: "settings",
          entity_name: "سعر الدولار",
          description: `تحديث سعر الدولار من ${oldRate} إلى ${finalRate}`,
          metadata: { old_rate: oldRate, new_rate: finalRate },
        }, { headers }).catch(() => {});
      }
      await refresh();
      toast.success(`تم تحديث سعر الدولار إلى ${finalRate.toLocaleString()}`);
      setOpen(false);
    } catch (e) {
      toast.error("فشل التحديث، حاول مجدداً");
    } finally {
      setSaving(false);
    }
  };

  const handleModeChange = async (mode) => {
    await setMode(mode);
    const m = MODES.find((x) => x.id === mode);
    toast.success(`تم تبديل العرض: ${m?.label}`);
  };

  const isHidden = displayMode === "iqd";

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          data-testid="currency-quick-edit-trigger"
          className={`h-9 gap-1.5 bg-slate-800/80 border-emerald-500/30 hover:border-emerald-500 hover:bg-emerald-500/10 font-mono text-xs px-2.5 ${
            isHidden ? "text-slate-400" : "text-emerald-400"
          }`}
        >
          <DollarSign className="w-3.5 h-3.5" />
          <span dir="ltr" className="font-bold">
            {isHidden ? "—" : Number(rate).toLocaleString()}
          </span>
          {!compact && !isHidden && (
            <span className="text-slate-400 text-[10px] hidden sm:inline">د.ع/$</span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-80 bg-slate-900 border-slate-700 text-white"
        dir="rtl"
        align="end"
      >
        <div className="space-y-4">
          {/* Mode Toggle */}
          <div>
            <p className="text-[11px] text-slate-400 mb-2 font-medium">طريقة العرض:</p>
            <div className="grid grid-cols-3 gap-1.5">
              {MODES.map((m) => (
                <button
                  key={m.id}
                  onClick={() => handleModeChange(m.id)}
                  data-testid={`currency-mode-${m.id}`}
                  className={`flex flex-col items-center gap-0.5 py-2 px-1 rounded-md border transition-all ${
                    displayMode === m.id
                      ? "bg-emerald-500/20 border-emerald-500 text-emerald-300"
                      : "bg-slate-800 border-slate-700 hover:border-emerald-500/50 text-slate-300"
                  }`}
                >
                  <span className="text-base leading-none">{m.emoji}</span>
                  <span className="text-[11px] font-bold">{m.label}</span>
                  <span className="text-[9px] text-slate-500 leading-tight">{m.desc}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Rate Editor - only shown when USD is enabled */}
          {!isHidden && (
            <>
              <div className="border-t border-slate-700" />

              <div className="flex items-center justify-between">
                <h4 className="font-bold text-sm flex items-center gap-2">
                  <DollarSign className="w-4 h-4 text-emerald-500" />
                  سعر الدولار
                </h4>
                <span className="text-[10px] text-slate-400">د.ع لكل $1</span>
              </div>

              <div className="flex gap-2">
                <Input
                  type="number"
                  value={value}
                  onChange={(e) => setValue(parseFloat(e.target.value) || 0)}
                  dir="ltr"
                  min="100"
                  step="1"
                  className="bg-slate-800 border-slate-700 text-white font-mono"
                  data-testid="currency-quick-edit-input"
                />
                <Button
                  onClick={() => save(value)}
                  disabled={saving || value === rate}
                  size="sm"
                  className="bg-emerald-600 hover:bg-emerald-700"
                  data-testid="currency-quick-edit-save"
                >
                  {saving ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Save className="w-4 h-4" />
                  )}
                </Button>
              </div>

              <div>
                <p className="text-[11px] text-slate-400 mb-1.5">قيم سريعة:</p>
                <div className="flex flex-wrap gap-1.5">
                  {PRESETS.map((p) => (
                    <button
                      key={p}
                      onClick={() => save(p)}
                      disabled={saving}
                      data-testid={`currency-preset-${p}`}
                      className={`text-xs px-2.5 py-1 rounded-md font-mono transition-all border ${
                        p === Number(rate)
                          ? "bg-emerald-500/20 border-emerald-500 text-emerald-300"
                          : "bg-slate-800 border-slate-700 hover:border-emerald-500/50 text-slate-300"
                      }`}
                    >
                      {p.toLocaleString()}
                    </button>
                  ))}
                </div>
              </div>
            </>
          )}

          <div className="bg-slate-800/50 rounded-md p-2 text-[10px] text-slate-400 leading-relaxed">
            💡 التبديل يحفظ تلقائياً وينعكس على كل صفحات التطبيق فوراً.
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
